import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { areaAEn, areaAAr } from './i18n-a';
import { areaBEn, areaBAr } from './i18n-b';

/**
 * English (the default) and Arabic, with full right-to-left. Arabic flips `dir` on <html>, which drives
 * every logical-property utility in the layout. The dictionary is flat and typed: a key missing in Arabic
 * is a compile error, never a blank in production. Pages keep their words in their own area files.
 */

export type Locale = 'en' | 'ar';

const base = {
  'app.name': 'Viganium Executive Dashboard',
  'app.short': 'Executive Dashboard',
  'app.tagline': 'For the company’s Administrators',

  'nav.overview': 'Executive Overview',
  'nav.employees': 'Employee Performance',
  'nav.points': 'Points & Rewards',
  'nav.work': 'Work & Productivity',
  'nav.projects': 'Projects & Clients',
  'nav.finance': 'Financial Intelligence',
  'nav.insights': 'AI Insights',
  'nav.reports': 'Executive Reports',
  'nav.access': 'Dashboard Access',
  'nav.settings': 'Dashboard Settings',
  'nav.groupCompany': 'Company',
  'nav.groupPeople': 'People & work',
  'nav.groupMoney': 'Money',
  'nav.groupBriefings': 'Briefings',
  'nav.groupAdmin': 'Administration',
  'nav.menu': 'Menu',
  'nav.closeMenu': 'Close the menu',

  'common.loading': 'Loading…',
  'common.error': 'Something went wrong',
  'common.retry': 'Retry',
  'common.close': 'Close',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.total': 'Total',
  'common.noResults': 'Nothing to show',
  'common.noResultsHint': 'Try another period or fewer filters.',
  'common.notFoundHint': 'This page does not exist.',
  'common.backHome': 'Back to the overview',
  'common.all': 'All',
  'common.search': 'Search',
  'common.apply': 'Apply',
  'common.clear': 'Clear',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.none': 'None',
  'common.unknown': 'Unknown',
  'common.view': 'View',
  'common.details': 'Details',
  'common.department': 'Department',
  'common.employee': 'Employee',
  'common.project': 'Project',
  'common.client': 'Client',
  'common.status': 'Status',
  'common.priority': 'Priority',
  'common.date': 'Date',
  'common.from': 'From',
  'common.to': 'To',
  'common.count': 'Count',
  'common.amount': 'Amount',
  'common.currency': 'Currency',
  'common.internal': 'Internal',
  'common.exportCsv': 'Export CSV',
  'common.exportPdf': 'Export PDF',
  'common.readOnly': 'Read-only',
  'common.formerEmployee': 'Former employee',
  'common.page': 'Page',
  'common.of': 'of',

  'theme.toLight': 'Switch to light theme',
  'theme.toDark': 'Switch to dark theme',
  'lang.switch': 'العربية',

  'account.signOut': 'Sign out',
  'account.signedInAs': 'Signed in as {name}',

  'auth.subtitle': 'The company’s figures, for its Administrators.',
  'auth.welcome': 'Sign in',
  'auth.emailLabel': 'Email',
  'auth.passwordLabel': 'Password',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in…',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.sameCredentials': 'Use your admin email and password.',
  'auth.notEligible':
    'This account cannot open the Executive Dashboard. It takes an active Administrator who has been given Dashboard Access in the admin (Settings › Executive Dashboard).',
  'auth.sessionEnded': 'Your session ended — your Dashboard Access may have been revoked. Sign in again.',
  'auth.forgotTitle': 'Reset your password',
  'auth.forgotIntro': 'Enter the email you sign in with. If it has an account, we will email it a link to set a new password.',
  'auth.sendResetLink': 'Send the link',
  'auth.resetLinkSent': 'If {email} has an account, a link to set a new password is on its way.',
  'auth.resetLinkSentHint': 'It works once, within the hour. It is the same password as the admin’s.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.resetTitle': 'Set a new password',
  'auth.resetIntro': 'Choose a new password. It is one password for the admin, Finance and this dashboard.',
  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm the password',
  'auth.passwordHint': 'At least {n} characters.',
  'auth.passwordsDiffer': 'The two passwords are not the same.',
  'auth.setNewPassword': 'Set the password',
  'auth.passwordResetDone': 'Your password is set. Sign in with it.',
  'auth.resetLinkInvalid': 'This reset link is invalid or has expired.',
  'auth.requestNewLink': 'Ask for a new link',
  'auth.showPassword': 'Show the password',
  'auth.hidePassword': 'Hide the password',

  // Periods and their status
  'period.label': 'Period',
  'period.type.DAILY': 'Day',
  'period.type.WEEKLY': 'Week',
  'period.type.MONTHLY': 'Month',
  'period.type.CUSTOM': 'Range',
  'period.preset.TODAY': 'Today',
  'period.preset.YESTERDAY': 'Yesterday',
  'period.preset.THIS_WEEK': 'This week',
  'period.preset.LAST_WEEK': 'Last week',
  'period.preset.THIS_MONTH': 'This month',
  'period.preset.LAST_MONTH': 'Last month',
  'period.preset.LAST_90_DAYS': 'Last 90 days',
  'period.preset.THIS_YEAR': 'This year',
  'period.custom': 'Custom…',
  'period.compareWith': 'Compared with {range}',
  'period.basis.PREVIOUS_DAY': 'the day before',
  'period.basis.PREVIOUS_WEEK': 'the week before',
  'period.basis.PREVIOUS_MONTH': 'the month before',
  'period.basis.SAME_DAYS_PREVIOUS_WEEK': 'the same days of last week',
  'period.basis.SAME_DAYS_PREVIOUS_MONTH': 'the same days of last month',
  'period.basis.PREVIOUS_RANGE': 'the range just before',
  'period.timezone': 'Company time ({tz})',
  'period.asOf': 'As of {time}',
  'status.LIVE': 'Live',
  'status.PROVISIONAL': 'Provisional',
  'status.FINALIZED': 'Finalized',
  'status.RECALCULATED': 'Recalculated',
  'status.liveHint': 'This period runs into today: its figures are live and will change.',
  'status.provisionalHint': 'Computed now; its sources may still change.',

  // States every page uses
  'state.notEnoughEvidence': 'Not enough evidence',
  'state.notMeasurable': 'Not measurable',
  'state.partial': 'Partial',
  'state.unavailable': 'Unavailable',
  'state.suppressed': 'Not shown',
  'state.suppressedSmallCohort': 'Not shown: fewer than {n} people were paid, so this would be someone’s pay.',
  'state.unconverted': 'Not converted: {currencies} (no rate) — left out, never added as zero.',
  'state.atTodayRate': 'Includes amounts valued at today’s rate.',
  'state.aiUnavailable': 'AI explanation unavailable',
  'state.aiDisabled': 'AI explanations are switched off.',
  'state.aiNotConfigured': 'AI is not set up on the server.',
  'state.aiBudget': 'This month’s AI budget is spent.',
  'state.aiPending': 'AI explanation being written…',
  'state.aiFailed': 'The AI explanation could not be made.',
  'state.aiNotRequested': 'No AI explanation yet.',
  'state.aiLabel': 'AI-generated analysis — verify against the sources.',
  'state.truncated': 'Some evidence was beyond the read limit: these figures may be incomplete.',
  'state.noData': 'No data for this period.',
  'state.change': '{sign}{value} vs {basis}',
  'state.noComparison': 'No comparison',

  // Finance statuses (Finance's own)
  'trust.VERIFIED': 'Verified',
  'trust.PREVIEW': 'Preview',
  'trust.UNVERIFIED': 'Unverified',
  'trust.PARTIAL': 'Partial',
  'trust.NOT_RECONCILED': 'Not reconciled',
  'trust.SETUP_NEEDED': 'Setup needed',
  'trust.UNAVAILABLE': 'Unavailable',
  'trust.RESTRICTED': 'Restricted',
} as const;

const baseAr: Record<keyof typeof base, string> = {
  'app.name': 'لوحة فيجانيوم التنفيذية',
  'app.short': 'اللوحة التنفيذية',
  'app.tagline': 'لمديري الشركة',

  'nav.overview': 'النظرة التنفيذية',
  'nav.employees': 'أداء الموظفين',
  'nav.points': 'النقاط والمكافآت',
  'nav.work': 'العمل والإنتاجية',
  'nav.projects': 'المشاريع والعملاء',
  'nav.finance': 'الذكاء المالي',
  'nav.insights': 'رؤى الذكاء الاصطناعي',
  'nav.reports': 'التقارير التنفيذية',
  'nav.access': 'صلاحية الوصول للوحة',
  'nav.settings': 'إعدادات اللوحة',
  'nav.groupCompany': 'الشركة',
  'nav.groupPeople': 'الأفراد والعمل',
  'nav.groupMoney': 'المال',
  'nav.groupBriefings': 'الملخصات',
  'nav.groupAdmin': 'الإدارة',
  'nav.menu': 'القائمة',
  'nav.closeMenu': 'إغلاق القائمة',

  'common.loading': 'جارٍ التحميل…',
  'common.error': 'حدث خطأ ما',
  'common.retry': 'إعادة المحاولة',
  'common.close': 'إغلاق',
  'common.next': 'التالي',
  'common.previous': 'السابق',
  'common.total': 'الإجمالي',
  'common.noResults': 'لا يوجد ما يُعرض',
  'common.noResultsHint': 'جرّب فترة أخرى أو عوامل تصفية أقل.',
  'common.notFoundHint': 'هذه الصفحة غير موجودة.',
  'common.backHome': 'العودة إلى النظرة التنفيذية',
  'common.all': 'الكل',
  'common.search': 'بحث',
  'common.apply': 'تطبيق',
  'common.clear': 'مسح',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.none': 'لا شيء',
  'common.unknown': 'غير معروف',
  'common.view': 'عرض',
  'common.details': 'التفاصيل',
  'common.department': 'القسم',
  'common.employee': 'الموظف',
  'common.project': 'المشروع',
  'common.client': 'العميل',
  'common.status': 'الحالة',
  'common.priority': 'الأولوية',
  'common.date': 'التاريخ',
  'common.from': 'من',
  'common.to': 'إلى',
  'common.count': 'العدد',
  'common.amount': 'المبلغ',
  'common.currency': 'العملة',
  'common.internal': 'داخلي',
  'common.exportCsv': 'تصدير CSV',
  'common.exportPdf': 'تصدير PDF',
  'common.readOnly': 'للقراءة فقط',
  'common.formerEmployee': 'موظف سابق',
  'common.page': 'صفحة',
  'common.of': 'من',

  'theme.toLight': 'التبديل إلى الوضع الفاتح',
  'theme.toDark': 'التبديل إلى الوضع الداكن',
  'lang.switch': 'English',

  'account.signOut': 'تسجيل الخروج',
  'account.signedInAs': 'مسجّل الدخول باسم {name}',

  'auth.subtitle': 'أرقام الشركة لمديريها.',
  'auth.welcome': 'تسجيل الدخول',
  'auth.emailLabel': 'البريد الإلكتروني',
  'auth.passwordLabel': 'كلمة المرور',
  'auth.signIn': 'تسجيل الدخول',
  'auth.signingIn': 'جارٍ تسجيل الدخول…',
  'auth.forgotPassword': 'نسيت كلمة المرور؟',
  'auth.sameCredentials': 'استخدم بريدك الإلكتروني وكلمة المرور الخاصة بلوحة الإدارة.',
  'auth.notEligible':
    'لا يمكن لهذا الحساب فتح اللوحة التنفيذية. يلزم مدير نشط مُنح صلاحية الوصول للوحة من لوحة الإدارة (الإعدادات › اللوحة التنفيذية).',
  'auth.sessionEnded': 'انتهت جلستك — ربما سُحبت صلاحية وصولك للوحة. سجّل الدخول مجددًا.',
  'auth.forgotTitle': 'إعادة تعيين كلمة المرور',
  'auth.forgotIntro': 'أدخل البريد الإلكتروني الذي تسجّل الدخول به. إن كان له حساب، فسنرسل إليه رابطًا لتعيين كلمة مرور جديدة.',
  'auth.sendResetLink': 'إرسال الرابط',
  'auth.resetLinkSent': 'إن كان للبريد {email} حساب، فرابط تعيين كلمة مرور جديدة في طريقه إليه.',
  'auth.resetLinkSentHint': 'يعمل مرة واحدة خلال ساعة. وهي كلمة المرور نفسها الخاصة بلوحة الإدارة.',
  'auth.backToSignIn': 'العودة لتسجيل الدخول',
  'auth.resetTitle': 'تعيين كلمة مرور جديدة',
  'auth.resetIntro': 'اختر كلمة مرور جديدة. وهي كلمة مرور واحدة للوحة الإدارة والمالية وهذه اللوحة.',
  'auth.newPassword': 'كلمة المرور الجديدة',
  'auth.confirmPassword': 'تأكيد كلمة المرور',
  'auth.passwordHint': '{n} أحرف على الأقل.',
  'auth.passwordsDiffer': 'كلمتا المرور غير متطابقتين.',
  'auth.setNewPassword': 'تعيين كلمة المرور',
  'auth.passwordResetDone': 'تم تعيين كلمة المرور. سجّل الدخول بها.',
  'auth.resetLinkInvalid': 'رابط إعادة التعيين غير صالح أو انتهت صلاحيته.',
  'auth.requestNewLink': 'طلب رابط جديد',
  'auth.showPassword': 'إظهار كلمة المرور',
  'auth.hidePassword': 'إخفاء كلمة المرور',

  'period.label': 'الفترة',
  'period.type.DAILY': 'يوم',
  'period.type.WEEKLY': 'أسبوع',
  'period.type.MONTHLY': 'شهر',
  'period.type.CUSTOM': 'فترة',
  'period.preset.TODAY': 'اليوم',
  'period.preset.YESTERDAY': 'أمس',
  'period.preset.THIS_WEEK': 'هذا الأسبوع',
  'period.preset.LAST_WEEK': 'الأسبوع الماضي',
  'period.preset.THIS_MONTH': 'هذا الشهر',
  'period.preset.LAST_MONTH': 'الشهر الماضي',
  'period.preset.LAST_90_DAYS': 'آخر 90 يومًا',
  'period.preset.THIS_YEAR': 'هذا العام',
  'period.custom': 'مخصص…',
  'period.compareWith': 'مقارنةً بـ{range}',
  'period.basis.PREVIOUS_DAY': 'اليوم السابق',
  'period.basis.PREVIOUS_WEEK': 'الأسبوع السابق',
  'period.basis.PREVIOUS_MONTH': 'الشهر السابق',
  'period.basis.SAME_DAYS_PREVIOUS_WEEK': 'الأيام نفسها من الأسبوع الماضي',
  'period.basis.SAME_DAYS_PREVIOUS_MONTH': 'الأيام نفسها من الشهر الماضي',
  'period.basis.PREVIOUS_RANGE': 'الفترة السابقة لها مباشرة',
  'period.timezone': 'توقيت الشركة ({tz})',
  'period.asOf': 'حتى {time}',
  'status.LIVE': 'مباشر',
  'status.PROVISIONAL': 'مبدئي',
  'status.FINALIZED': 'نهائي',
  'status.RECALCULATED': 'أعيد حسابه',
  'status.liveHint': 'تمتد هذه الفترة حتى اليوم: أرقامها مباشرة وستتغير.',
  'status.provisionalHint': 'حُسبت الآن؛ وقد تتغير مصادرها.',

  'state.notEnoughEvidence': 'لا توجد أدلة كافية',
  'state.notMeasurable': 'غير قابل للقياس',
  'state.partial': 'جزئي',
  'state.unavailable': 'غير متاح',
  'state.suppressed': 'غير معروض',
  'state.suppressedSmallCohort': 'غير معروض: صُرفت رواتب لأقل من {n} أشخاص، فسيكشف ذلك راتب شخص بعينه.',
  'state.unconverted': 'غير محوّل: {currencies} (لا يوجد سعر) — مستبعد ولا يُحتسب صفرًا.',
  'state.atTodayRate': 'يتضمن مبالغ مقوّمة بسعر اليوم.',
  'state.aiUnavailable': 'تحليل الذكاء الاصطناعي غير متاح',
  'state.aiDisabled': 'تحليلات الذكاء الاصطناعي متوقفة.',
  'state.aiNotConfigured': 'الذكاء الاصطناعي غير مُعد على الخادم.',
  'state.aiBudget': 'نفدت ميزانية الذكاء الاصطناعي لهذا الشهر.',
  'state.aiPending': 'يُكتب تحليل الذكاء الاصطناعي…',
  'state.aiFailed': 'تعذّر إعداد تحليل الذكاء الاصطناعي.',
  'state.aiNotRequested': 'لا يوجد تحليل بالذكاء الاصطناعي بعد.',
  'state.aiLabel': 'تحليل من إنشاء الذكاء الاصطناعي — تحقق منه مقابل المصادر.',
  'state.truncated': 'تجاوز بعض الأدلة حد القراءة: قد تكون هذه الأرقام غير مكتملة.',
  'state.noData': 'لا توجد بيانات لهذه الفترة.',
  'state.change': '{sign}{value} مقارنةً بـ{basis}',
  'state.noComparison': 'لا توجد مقارنة',

  'trust.VERIFIED': 'موثّق',
  'trust.PREVIEW': 'معاينة',
  'trust.UNVERIFIED': 'غير موثّق',
  'trust.PARTIAL': 'جزئي',
  'trust.NOT_RECONCILED': 'غير مطابق',
  'trust.SETUP_NEEDED': 'يلزم الإعداد',
  'trust.UNAVAILABLE': 'غير متاح',
  'trust.RESTRICTED': 'مقيّد',
};

const en = { ...base, ...areaAEn, ...areaBEn };
export type TranslationKey = keyof typeof en;
const ar: Record<TranslationKey, string> = { ...baseAr, ...areaAAr, ...areaBAr };

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, ar };

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** Has the dictionary this key? For codes the API sends (finding codes, notices, risks). */
  has: (key: string) => key is TranslationKey;
  /** Picks the Arabic field when the UI is Arabic and one exists. */
  localized: (
    row: { name?: string | null; nameAr?: string | null; fullName?: string | null; fullNameAr?: string | null; companyName?: string | null; companyNameAr?: string | null } | null | undefined,
  ) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);
const LOCALE_KEY = 'viganium.dashboard.locale';

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    return saved === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch {
      // A private window: the language is kept for this visit only.
    }
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);
  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text: string = DICTIONARIES[locale][key] ?? en[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) text = text.split(`{${k}}`).join(String(v));
      return text;
    },
    [locale],
  );
  const has = useCallback((key: string): key is TranslationKey => key in en, []);
  const localized = useCallback<I18nContextValue['localized']>(
    (row) => {
      if (!row) return '—';
      if (locale === 'ar') return row.nameAr || row.fullNameAr || row.companyNameAr || row.name || row.fullName || row.companyName || '—';
      return row.name || row.fullName || row.companyName || '—';
    },
    [locale],
  );
  const value = useMemo(() => ({ locale, dir, setLocale, t, has, localized }), [locale, dir, setLocale, t, has, localized]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>.');
  return ctx;
}

/** Enum labels from the API (SCREAMING_SNAKE), readable in both languages. */
const ENUM_AR: Record<string, string> = {
  NEW: 'جديدة', ASSIGNED: 'مُسندة', IN_PROGRESS: 'قيد التنفيذ', WAITING_REQUESTER: 'بانتظار مقدّم الطلب',
  RESOLVED: 'تم الحل', CLOSED: 'مغلقة', REOPENED: 'أُعيد فتحها', CANCELLED: 'ملغاة',
  TO_DO: 'قيد الانتظار', BLOCKED: 'معلّقة', REVIEW: 'مراجعة', DONE: 'منجزة',
  QUEUED: 'في الطابور', AVAILABLE: 'متاحة', SKIPPED: 'متخطاة', CONVERTED: 'محوّلة',
  LOW: 'منخفضة', MEDIUM: 'متوسطة', HIGH: 'عالية', URGENT: 'عاجلة',
  DRAFT: 'مسودة', ACTIVE: 'نشط', TESTING: 'قيد الاختبار', ON_HOLD: 'متوقف', SUPPORT: 'قيد الدعم', COMPLETED: 'مكتمل', ARCHIVED: 'مؤرشف',
  APPROVED: 'معتمد', PAID: 'مدفوع', PENDING: 'قيد الانتظار', ISSUED: 'صادرة', PARTIALLY_PAID: 'مدفوعة جزئيًا', OVERDUE: 'متأخرة',
  GRANTED: 'ممنوحة', PENDING_SETTLEMENT: 'بانتظار التسوية', TERMINATED: 'منتهٍ', PAST_DUE: 'متأخر السداد',
  MONTHLY: 'شهري', YEARLY: 'سنوي', ONE_TIME: 'مرة واحدة', DAILY: 'يومي', WEEKLY: 'أسبوعي', CUSTOM: 'مخصص',
  READY: 'جاهز', RUNNING: 'قيد التنفيذ', FAILED: 'فشل', DISABLED: 'متوقف', BUDGET_EXCEEDED: 'تجاوز الميزانية',
  SYSTEM: 'النظام', MANUAL: 'يدوي', INACTIVE: 'غير نشط',
};

export function enumLabel(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  if (locale === 'ar' && ENUM_AR[value]) return ENUM_AR[value];
  return value
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}
